/**
 * **宿主能力契约**：谁在跑这份文档，能跑哪一部分。
 *
 * 背景见 `docs/runtime-hosts.md`。一句话：
 *
 * > 编辑器不需要变成 n8n；它需要产出**能在别的宿主里跑的东西**。
 *
 * 于是"编辑器要不要一直开着"从一个架构难题，变成了一句**产品说明**：
 * 文档（工具）声明自己需要什么能力与什么触发方式，宿主声明自己提供什么，
 * 两者一比对，**要么跑，要么明确拒绝并说清缺什么**。
 *
 * ⚠️ 这一层刻意只有**类型 + 一个纯函数**（`hostCanRun`）：
 * 它是"以后加常驻宿主不返工"的唯一保证，所以现在就要定死形状，
 * 但**不实现**任何宿主（浏览器宿主的能力表在 `BROWSER_HOST` 里，
 * 常驻宿主的实现推迟到真有无人值守场景时）。
 */

/**
 * 宿主能提供的能力（**封闭列表**：加新的要回来改这里，是故意的）。
 *
 * ⚠️ 每一项都对应"文档里会出现的那类副作用"，而不是某个 API：
 * `net` 不是 `fetch`（侧车宿主也能实现 `net`，但没有 CORS 限制）。
 */
export type HostCapability =
  /** 访问网络（浏览器里受 CORS 限制；侧车没有） */
  | 'net'
  /** 读写本地文件（浏览器做不到） */
  | 'fs'
  /** 常驻定时（浏览器只在页面打开时有效，且后台标签会被降频） */
  | 'timer'
  /** 接收入站请求（webhook；浏览器做不到） */
  | 'webhook'
  /** 操作打印设备（只存在于执行端） */
  | 'device'
  /** 剪贴板 */
  | 'clipboard'
  /** 起子进程 / 调用本机命令（只存在于侧车/桌面） */
  | 'shell'

/** 事件源的种类 = "什么能让图跑起来" */
export type TriggerKind =
  /** 手动：按钮 / 运行菜单 —— 浏览器里就能做 */
  | 'manual'
  /** 打开工具/文档时 —— 浏览器里就能做 */
  | 'startup'
  /** 工具界面上的交互（提交表单 / 值变化）—— 浏览器里就能做 */
  | 'ui-event'
  /** 定时（间隔 / 延时）—— 浏览器里只有"页面开着"时有效 */
  | 'timer'
  /** 固定时刻（每天 8 点）—— 需要常驻宿主 */
  | 'schedule'
  /** 入站请求 —— 需要常驻宿主且公网可达 */
  | 'webhook'
  /** 文件变化 —— 需要常驻宿主 */
  | 'file'

/** 一份文档（工具）声明自己需要什么 */
export interface HostRequirement {
  capabilities: readonly HostCapability[]
  triggers: readonly TriggerKind[]
}

/** 一个宿主声明自己能提供什么 */
export interface HostDescriptor {
  /** 稳定标识（诊断与工具卡片上显示） */
  id: 'browser' | 'sidecar' | 'desktop' | 'cloud'
  /** 给人看的名字 */
  label: string
  capabilities: readonly HostCapability[]
  triggers: readonly TriggerKind[]
  /**
   * **这个宿主的限制**（人话，直接显示在工具卡片上）。
   *
   * ⚠️ 这一栏是**必须**的：宿主最容易被误解的地方不是"不支持什么"，
   * 而是"支持但有限制"（比如浏览器支持定时器，但页面一关就停、
   * 后台标签还会被降频到 ≥1 分钟）。不写清楚，用户会以为它坏了。
   */
  limits: readonly string[]
}

export interface HostCheck {
  ok: boolean
  /** 缺哪些能力（人话，能直接显示） */
  missingCapabilities: readonly HostCapability[]
  /** 缺哪些触发方式 */
  missingTriggers: readonly TriggerKind[]
}

/**
 * **这份文档能在这个宿主里跑吗** —— 不能跑要说清缺什么。
 *
 * ⚠️ 这是"缺包/缺能力必须**拒绝执行**"那条规矩的落点
 * （`print-template-architecture.md` §2.8：生产端没有人看着，静默降级最危险）。
 * 所以这里**只报事实**，不做任何"尽力而为"的降级 ——
 * 降级与否由产品层决定（例如"定时器降级成手动运行"），不在契约里偷偷发生。
 */
export function hostCanRun(host: HostDescriptor, need: HostRequirement): HostCheck {
  const missingCapabilities = need.capabilities.filter((c) => !host.capabilities.includes(c))
  const missingTriggers = need.triggers.filter((t) => !host.triggers.includes(t))
  return {
    ok: missingCapabilities.length === 0 && missingTriggers.length === 0,
    missingCapabilities,
    missingTriggers,
  }
}

/** 一句话说明"为什么跑不了"（工具卡片 / 运行前的提示用它） */
export function describeHostCheck(host: HostDescriptor, need: HostRequirement): string {
  const check = hostCanRun(host, need)
  if (check.ok) return `可以在「${host.label}」里运行`
  const parts: string[] = []
  if (check.missingCapabilities.length > 0) {
    parts.push(`缺能力：${check.missingCapabilities.join('、')}`)
  }
  if (check.missingTriggers.length > 0) {
    parts.push(`缺触发方式：${check.missingTriggers.join('、')}`)
  }
  return `「${host.label}」跑不了这份文档 —— ${parts.join('；')}`
}

/**
 * **浏览器宿主**（今天的默认宿主）。
 *
 * 它支持的东西恰好是"工具箱"需要的那一档：手动运行 + 打开时运行 +
 * 界面交互 + 页面内的定时器。**没有** `webhook` / `fs` / `shell` / `device`。
 */
export const BROWSER_HOST: HostDescriptor = {
  id: 'browser',
  label: '浏览器',
  capabilities: ['net', 'timer', 'clipboard'],
  triggers: ['manual', 'startup', 'ui-event', 'timer'],
  limits: [
    '页面关掉就停（没有常驻进程）',
    '后台标签的定时器会被浏览器降频，间隔不短于 1 分钟',
    '网络请求受 CORS 限制，抓不了不放行跨域的站点',
    '收不到外部请求（没有 webhook）',
  ],
}

/** 侧车宿主（**还没做**；形状先摆在这儿，等真有无人值守场景时实现） */
export const SIDECAR_HOST: HostDescriptor = {
  id: 'sidecar',
  label: '本机运行端',
  capabilities: ['net', 'fs', 'timer', 'webhook', 'clipboard', 'shell'],
  triggers: ['manual', 'startup', 'ui-event', 'timer', 'schedule', 'webhook', 'file'],
  limits: ['需要在本机起一个进程（`node-print-run`）', '只在本机可达（要公网得自己暴露）'],
}
